import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects'

import {
  addFavoritePayment,
  BILL_PAYMENT_API_URL,
  removeFavoritePayment,
} from '@/api/BillPaymentApi'
import { IEnterTransactionPinForTransfer } from '@/api/BridgeApi'
import * as Bridge from '@/api/BridgeApi'
import { getCSRF } from '@/api/CsrfApi'
import {
  fetchSecureConfirmMessage,
  IGenerateSecureConfirmationMessageResponse,
} from '@/api/SecureConfirmApi'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import {
  EmptyBillPaymentPayResponse,
  IBillPaymentPayRequest,
  IBillPaymentPayResponse,
  PAYMENT_TYPE_CODE,
} from '@/domains/BillPaymentDomain'
import { BILL_PAYMENT_ROUTES, ENTRY_ROUTE_QUERY, STATUS_CODE } from '@/domains/CommonDomain'
import { SecureTransactionType } from '@/domains/SecureConfirmDomain'
import { IRootState } from '@/modules'
import { billPaymentAmountSelector } from '@/modules/bill-payment/BillPaymentAmountModule'
import {
  billPaymentMainSelector,
  IFlattenProductType,
} from '@/modules/bill-payment/BillPaymentMainModule'
import { errorActions } from '@/modules/common/error'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import { createPostAction } from '@/utils/actionUtils'
import { BRIDGE_ERROR_CODE } from '@/utils/bridgeUtils/constants'
import {
  HostType,
  IRequestPinAuthResult,
  RequestPinAuthAction,
} from '@/utils/bridgeUtils/features/requestPinAuth'
import { newError } from '@/utils/errorUtils'
import { push } from '@/utils/historyUtils'
import { $ } from '@/utils/i18nUtils'
import { toInteger } from '@/utils/numberUtils'
import { apiFailureReducer, reset } from '@/utils/reducerUtils'

export const BILL_PAYMENT_RESULT = 'BillPaymentResult'

const BILL_PAYMENT_PAY = `${BILL_PAYMENT_RESULT}/billPaymentPay`
const BILL_PAYMENT_RESULT_FAVORITE_ADD = `${BILL_PAYMENT_RESULT}/favorite/add`
const BILL_PAYMENT_RESULT_FAVORITE_REMOVE = `${BILL_PAYMENT_RESULT}/favorite/remove`
const CLEAR_STATE = `${BILL_PAYMENT_RESULT}/clearState`

interface IPayAction {
  amount: string
  fee: string
  customerId: string
  reserved: string
  productTypeCode: string
}

export interface IBillPaymentResultState {
  payResponse: IBillPaymentPayResponse | null
}

export const initialState: IBillPaymentResultState = {
  payResponse: null,
}

export const billResultActions = {
  clearState: createAction(CLEAR_STATE),
}

export const payAsync = createPostAction<IPayAction>(BILL_PAYMENT_PAY)
export const billResultFavoriteAddAsync = createPostAction(BILL_PAYMENT_RESULT_FAVORITE_ADD)
export const billResultFavoriteRemoveAsync = createPostAction(BILL_PAYMENT_RESULT_FAVORITE_REMOVE)

const payReducer = (state: IBillPaymentResultState, action: Action<any>) => ({
  ...state,
  payResponse: action.payload,
})

const reducer = {
  [payAsync.SUCCESS]: payReducer,
  [payAsync.FAILURE]: apiFailureReducer,
  [billResultFavoriteAddAsync.SUCCESS]: (state: IBillPaymentResultState) => ({
    ...state,
    payResponse: {
      ...state.payResponse,
      favorite: true,
    },
  }),
  [billResultFavoriteRemoveAsync.SUCCESS]: (state: IBillPaymentResultState) => ({
    ...state,
    payResponse: {
      ...state.payResponse,
      favorite: false,
    },
  }),
  [billResultFavoriteRemoveAsync.FAILURE]: apiFailureReducer,
  [CLEAR_STATE]: (state: IBillPaymentResultState) => reset(state, initialState),
}

export const billPaymentResultSelector = {
  payResponse: (state: IRootState): IBillPaymentPayResponse =>
    state[BILL_PAYMENT_RESULT].payResponse ?? EmptyBillPaymentPayResponse,
  isFavorite: (state: IRootState): boolean => billPaymentResultSelector.payResponse(state).favorite,
}

export const billPaymentResultReducer = handleActions(reducer, initialState)

// TODO 이거 정리 필요
export function* pay(action: Action<IPayAction>) {
  try {
    yield put(loadingActions.start(BILL_PAYMENT_RESULT))
    const { productTypeCode, customerId, reserved, amount, fee } = action.payload
    const flattenProductType: IFlattenProductType = yield select(
      billPaymentMainSelector.flattenProductType,
      productTypeCode,
    )
    const { paymentTypeCode, productTypeSwitchId } = flattenProductType

    const secureTransaction =
      paymentTypeCode === PAYMENT_TYPE_CODE.BILLS
        ? SecureTransactionType.BILL_PAYMENT_PAY_BILLS
        : SecureTransactionType.BILL_PAYMENT_PAY_PULSA

    const { acctNo } = yield select(billPaymentAmountSelector.selectedAccount)

    const payRequestPayload: IBillPaymentPayRequest = {
      acctNo,
      amount,
      customerId,
      reserved,
      productTypeCode,
      productTypeSwitchId,
    }
    const amountWithFee = toInteger(amount) + toInteger(fee)
    const secureConfirmationMessage: IGenerateSecureConfirmationMessageResponse = yield call(
      fetchSecureConfirmMessage,
      secureTransaction,
      { ...payRequestPayload, amount: `${amountWithFee}` },
    )

    const trxPinForTransferPayload: IEnterTransactionPinForTransfer = {
      hostType: HostType.BANK_API,
      apiUrl: BILL_PAYMENT_API_URL.BILL_PAY,
      request: {
        secureConfirmation: {
          ...secureConfirmationMessage,
          secureTransaction,
        },
        ...payRequestPayload,
      },
    }

    const { result }: IRequestPinAuthResult = yield call(
      Bridge.enterTransactionPinForTransfer,
      trxPinForTransferPayload,
    )

    if (!result) {
      throw newError(STATUS_CODE.UNKNOWN_CLIENT_ERROR)
    }
    if (result.action === RequestPinAuthAction.CLOSE) {
      return
    }
    if (
      result.action === RequestPinAuthAction.AUTH_FAIL ||
      result.action === RequestPinAuthAction.AUTH_ERROR ||
      result.action !== RequestPinAuthAction.AUTH_SUCCESS
    ) {
      // const { code } = JSON.parse(result.response)
      // throw newError(code)

      const { code, message } = JSON.parse(result.response)
      const err = {
        code: toInteger(code),
        message: message,
      }
      throw err
    }

    const billPaymentPayResponse: IBillPaymentPayResponse = JSON.parse(result.response)
    yield put(payAsync.success(billPaymentPayResponse))

    // TODO 이걸 빼고싶음.
    yield call(push, `${BILL_PAYMENT_ROUTES.RESULT}/${productTypeCode}?${ENTRY_ROUTE_QUERY}`)
  } catch (e) {
    if (e.code === BRIDGE_ERROR_CODE.SECURE_CONFIRMATION_CANCELED) {
      return
    } else if (e.code === STATUS_CODE.BILL_PAYMENT_TRANSACTION_FAIL) {
      // 청구서가 유효하지 않았다면
      // 청구서가 유효하지 않음을 알리는 경고 레이어를 표시
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_TRANSACTION_FAIL))
    } else if (e.code === STATUS_CODE.INVALID_ACCCOUNT_STATUS) {
      // 이체 중에 계좌의 상태 이상을 발견했다면
      // 계좌의 상태가 유효하지 않음을 알리는 경고 레이어를 표시
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_INVALID_ACCCOUNT_STATUS))
    } else if (e.code === STATUS_CODE.INSUFFICIENT_BALANCE) {
      // 이체 중에 계좌의 잔고 부족을 발견했다면
      // 계좌의 잔고 부족을 알리는 경고 레이어를 표시
      yield put(layerActions.open(LEAVE_ALERT_TYPE.BILL_PAYMENT_INSUFFICIENT_BALANCE))
    } else {
      yield put(errorActions.set(e))
    }
  } finally {
    yield put(loadingActions.finish(BILL_PAYMENT_RESULT))
  }
}

export function* addFavoriteAtBillPaymentResult() {
  try {
    yield put(loadingActions.start(BILL_PAYMENT_RESULT))
    yield call(getCSRF)
    const { trxPayKey } = yield select(billPaymentResultSelector.payResponse)

    yield call(addFavoritePayment, { trxPayKey })
    yield put(billResultFavoriteAddAsync.success())
  } catch (e) {
    switch (e.code) {
      case STATUS_CODE.MAXIMUM_FAVORITE_COUNT_OVER:
        yield call(Bridge.showNotification, {
          message: $('billpayment_main_favorite_exceederror_toast'),
        })
        break

      default:
        yield put(errorActions.set(e))
        break
    }
  } finally {
    yield put(loadingActions.finish(BILL_PAYMENT_RESULT))
  }
}

export function* removeFavoriteAtBillPaymentResult() {
  try {
    yield put(loadingActions.start(BILL_PAYMENT_RESULT))
    yield call(getCSRF)

    const { trxPayKey } = yield select(billPaymentResultSelector.payResponse)

    yield call(removeFavoritePayment, { trxPayKey })
    yield put(billResultFavoriteRemoveAsync.success())
  } catch (e) {
    yield put(errorActions.set(e))
  } finally {
    yield put(loadingActions.finish(BILL_PAYMENT_RESULT))
  }
}

export const billPaymentResultSaga = [
  takeLeading(payAsync.POST, pay),
  takeLatest(billResultFavoriteAddAsync.POST, addFavoriteAtBillPaymentResult),
  takeLatest(billResultFavoriteRemoveAsync.POST, removeFavoriteAtBillPaymentResult),
]
